/**
 * PricingRulePromotionSummary — the "command center" card at the top of the main
 * column (StandardDetailPage's `renderAboveMain`), Thai-first.
 *
 *   1. Status line   — running? coupon or automatic?
 *   2. Hero          — coupon rules get a ticket + code + actions + usage metrics
 *                      (CouponHeroBlock); automatic rules get a clean icon hero
 *   3. Detail list   — applies-to / dates / minimum / store, labelled + scannable
 * All from the loaded doc + linked coupon, no extra fetch.
 */

import { Gift, Percent, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { useLanguage } from "@/i18n/useLanguage";
import {
  rewardSummary,
  appliesToSummary,
  promotionStatus,
  perkChips,
  hasCoreReward,
  isCouponRule,
  isProductReward,
  type StatusTone,
} from "./pricingRuleDetailModel";
import { usePricingRuleCoupons } from "./usePricingRuleCoupons";
import { CouponHeroBlock } from "./CouponHeroBlock";
import { DuplicatePromoButton } from "./DuplicatePromoButton";
import {
  DateRangeRow,
  MinSpendRow,
  StoreScopeRow,
  FieldRow,
  RowDivider,
  AppliesToProductsRow,
} from "./PricingRuleEditableRows";

const STATUS_STYLES: Record<StatusTone, string> = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20",
  scheduled: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:ring-sky-500/20",
  expired: "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-white/5 dark:text-gray-400 dark:ring-white/10",
  disabled: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20",
};

function Pill({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function PricingRulePromotionSummary({
  formData,
  docName,
}: {
  formData: Record<string, any>;
  docName: string;
}) {
  const { language: lang } = useLanguage();
  const th = lang === "th";

  const status = promotionStatus(formData, undefined, lang);
  const coupon = isCouponRule(formData);
  const { data: coupons } = usePricingRuleCoupons(docName, coupon);
  const linked = coupons?.[0];

  // Perks show as a separate line only when they accompany a core reward.
  const core = hasCoreReward(formData);
  const perks = core ? perkChips(formData, lang) : [];

  const RewardIcon = isProductReward(formData) ? Gift : core ? Percent : Sparkles;

  const company = formData.company || "—";

  return (
    <div className={cn(CARD, "p-5")}>
      {/* 1. Status */}
      <div className="flex flex-wrap items-center gap-2">
        <Pill className={STATUS_STYLES[status.tone]}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {status.label}
        </Pill>
        <Pill className="bg-gray-100 text-gray-600 ring-gray-200 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10">
          {coupon ? (th ? "โค้ดคูปอง" : "Coupon code") : th ? "อัตโนมัติ" : "Automatic"}
        </Pill>
        {formData.priority !== undefined && formData.priority !== null && formData.priority !== "" && (
          <Pill className="bg-gray-100 text-gray-600 ring-gray-200 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10">
            {th ? `ลำดับ ${formData.priority}` : `Priority ${formData.priority}`}
          </Pill>
        )}
      </div>

      {/* 2. Hero */}
      <div className="mt-4">
        {coupon && linked ? (
          <CouponHeroBlock formData={formData} coupon={linked} lang={lang} />
        ) : (
          <div className="flex items-start gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
              <RewardIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {th ? "สิ่งที่ลูกค้าจะได้รับ" : "What customers get"}
              </div>
              <div className="mt-1 text-[22px] font-bold leading-tight text-gray-900 dark:text-gray-100 break-words">
                {rewardSummary(formData, lang)}
              </div>
            </div>
          </div>
        )}

        {/* perks that accompany a discount/free-item reward */}
        {perks.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] text-gray-500 dark:text-gray-400">{th ? "และ" : "plus"}</span>
            {perks.map((p) => (
              <Pill
                key={p}
                className="bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:ring-sky-500/20"
              >
                {p}
              </Pill>
            ))}
          </div>
        )}

        {/* Automatic rules have no coupon hero, so surface the clone action here.
            Coupon rules get their Duplicate button inside CouponHeroBlock. */}
        {!coupon && (
          <div className="mt-3 flex flex-wrap gap-2">
            <DuplicatePromoButton formData={formData} lang={lang} />
          </div>
        )}
      </div>

      {/* 3. The conditions — same generic inline-field row system for consistency.
             Dates & minimum are editable in place; product target and store are
             structural (edit via Details / ?generic=1). Edge-to-edge (-mx-5). */}
      <div className="-mx-5 mt-4 border-t border-border/40">
        {formData.apply_on === "Item Code" ? (
          <AppliesToProductsRow label={th ? "ใช้กับ" : "Applies to"} formData={formData} lang={lang} />
        ) : (
          <FieldRow label={th ? "ใช้กับ" : "Applies to"} value={appliesToSummary(formData, lang)} />
        )}
        <RowDivider />
        <DateRangeRow label={th ? "ระยะเวลา" : "Active dates"} formData={formData} docName={docName} lang={lang} />
        <RowDivider />
        <MinSpendRow label={th ? "ยอดขั้นต่ำ" : "Minimum spend"} formData={formData} docName={docName} lang={lang} />
        <RowDivider />
        <FieldRow label={th ? "ร้านค้า" : "Store"} value={company} />
        <RowDivider />
        <StoreScopeRow label={th ? "หน้าร้าน" : "Storefronts"} formData={formData} docName={docName} lang={lang} />
      </div>
    </div>
  );
}
